import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { blogAPI } from "../../lib/api";
import { FileText, Plus, Edit, Trash2, Eye, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const PILAR_META = {
    musica:          { label: "Música",          color: "text-violet-300", bg: "bg-violet-900/30" },
    fe:              { label: "Fé",              color: "text-amber-300",  bg: "bg-amber-900/30" },
    mulheres:        { label: "Mulheres",         color: "text-rose-300",   bg: "bg-rose-900/30" },
    proposito:       { label: "Propósito",        color: "text-sky-300",    bg: "bg-sky-900/30" },
    mentorias:       { label: "Mentorias",        color: "text-emerald-300",bg: "bg-emerald-900/30" },
    historia_pessoal:{ label: "História Pessoal", color: "text-orange-300", bg: "bg-orange-900/30" },
};

const PILARES = Object.entries(PILAR_META).map(([v, m]) => ({ value: v, ...m }));

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function BlogAdmin() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [pilarFilter, setPilarFilter] = useState("");
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try { const r = await blogAPI.list(); setPosts(r.data || []); }
        catch { toast.error("Erro ao carregar posts"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Excluir este post?")) return;
        try { await blogAPI.delete(id); toast.success("Post excluído"); load(); }
        catch { toast.error("Erro ao excluir"); }
    };

    const filtered = posts.filter(p => {
        const matchSearch = !search || p.title?.toLowerCase().includes(search.toLowerCase());
        const matchPilar  = !pilarFilter || p.pilar === pilarFilter;
        return matchSearch && matchPilar;
    });

    return (
        <>
        <Header title="Blog / CMS" subtitle={`${posts.length} posts`} actions={<Link to="/blog/editor" className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Novo Post</Link>} />
        <div className="p-6 space-y-5">

            {/* Filtros */}
            <div className="flex gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-stone-500" />
                    <input className="bg-transparent text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none flex-1"
                        placeholder="Buscar por título..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setPilarFilter("")}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${!pilarFilter ? "bg-amber-700 border-amber-600 text-white" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                        Todos
                    </button>
                    {PILARES.map(p => (
                        <button key={p.value} onClick={() => setPilarFilter(p.value)}
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${pilarFilter === p.value ? `${p.bg} border-current ${p.color}` : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Lista */}
            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : filtered.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <FileText className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhum post encontrado.</p>
                    <Link to="/blog/editor" className="text-amber-500 text-xs hover:text-amber-400 mt-2 inline-block">Criar primeiro post →</Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map(post => {
                        const pilar = PILAR_META[post.pilar] || { label: post.pilar, color: "text-stone-400", bg: "bg-stone-800" };
                        return (
                            <div key={post.id} className="bg-stone-900 border border-stone-800 rounded-xl p-4 flex items-center gap-4 hover:border-stone-700 transition-all group">
                                {post.cover_image && (
                                    <img src={post.cover_image} alt="" className="w-16 h-12 object-cover rounded-lg flex-shrink-0" />
                                )}
                                {!post.cover_image && (
                                    <div className="w-16 h-12 bg-stone-800 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-5 h-5 text-stone-600" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-medium text-stone-100 truncate">{post.title}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${pilar.bg} ${pilar.color}`}>{pilar.label}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${post.published ? "bg-green-900/30 text-green-300" : "bg-stone-800 text-stone-500"}`}>
                                            {post.published ? "Publicado" : "Rascunho"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-stone-500 mt-0.5 truncate">{post.excerpt || `/${post.slug}`}</p>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-stone-500">
                                    <span>{fmtDate(post.published_at || post.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Link to={`/blog/editor/${post.id}`}
                                        className="p-1.5 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-700 transition-all">
                                        <Edit className="w-3.5 h-3.5" />
                                    </Link>
                                    <button onClick={() => del(post.id)}
                                        className="p-1.5 rounded-lg text-stone-400 hover:text-red-400 hover:bg-red-900/20 transition-all">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
        </>
    );
}
